import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Plus, Trash2, Lock, X, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { locationsApi, formatError } from "../lib/api";
import { PremiumModal } from "./PremiumLockTile";

export const LocationsCard = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPremium, setShowPremium] = useState(false);

  const load = async () => {
    try {
      setLocations(await locationsApi.list());
    } catch {
      setLocations([]);
    }
  };
  useEffect(() => { load(); }, []);

  const handleAddClick = () => {
    if (!user?.is_premium) {
      setShowPremium(true);
      return;
    }
    setShowAdd(true);
  };

  const submitAdd = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await locationsApi.create(name.trim());
      toast.success(`Added "${name.trim()}"`);
      setName("");
      setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!confirm(`Delete "${loc.name}"? Containers there will move to the default location.`)) return;
    try {
      await locationsApi.remove(loc.id);
      toast.success("Location deleted");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="tup-card p-4 col-span-2" data-testid="locations-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#DCEDF0] flex items-center justify-center">
            <MapPin className="w-3.5 h-3.5 text-[#1F5566]" />
          </div>
          <h3 className="font-heading text-sm font-bold text-[#1F5566] uppercase tracking-wider">
            Storage Locations
          </h3>
        </div>
        {!user?.is_premium && (
          <span className="text-[10px] font-bold uppercase tracking-wider brand-gradient-bg text-white px-2 py-0.5 rounded-full flex items-center gap-1">
            <Lock className="w-3 h-3" /> Premium
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="locations-list">
        {locations.map((loc) => (
          <div
            key={loc.id}
            data-testid={`location-${loc.id}`}
            className="group flex items-center gap-1.5 bg-[#F5F3ED] px-3 py-1.5 rounded-full text-xs font-semibold text-[#1F5566]"
          >
            <MapPin className="w-3 h-3" />
            {loc.name}
            {loc.is_default && (
              <span className="text-[9px] uppercase font-bold text-[#8A93A6] ml-1">default</span>
            )}
            {!loc.is_default && (
              <button
                onClick={() => handleDelete(loc)}
                data-testid={`delete-location-${loc.id}`}
                className="ml-1 text-[#8A93A6] hover:text-[#9C3E1D] opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete location"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <Button
        onClick={handleAddClick}
        data-testid="add-location-btn"
        variant="outline"
        className="w-full mt-4 border-dashed border-[#C9C4B5] text-[#1F5566] hover:bg-[#F5F3ED] flex items-center justify-center gap-2"
      >
        {user?.is_premium ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
        {user?.is_premium ? "Add location" : "Unlock more locations"}
      </Button>

      <AnimatePresence>
        {showAdd && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={submitAdd}
            data-testid="add-location-form"
            className="mt-3 space-y-3 overflow-hidden"
          >
            <div>
              <Label className="text-[#1F5566] font-semibold mb-1.5 block text-xs uppercase tracking-wider">
                Location name
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Pantry, Garage Fridge"
                data-testid="new-location-input"
                autoFocus
                className="bg-white border-[#E2E0D8] h-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setShowAdd(false); setName(""); }}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="submit-location-btn"
                className="flex-1 brand-gradient-bg text-white"
              >
                <Check className="w-4 h-4 mr-1" />
                {busy ? "Adding…" : "Add"}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <PremiumModal open={showPremium} onClose={() => setShowPremium(false)} />
    </div>
  );
};
